import React from "react";
import { CmsField, CmsFieldset, CmsDateField, CmsSelectField } from "@/components/molecules";
import { GENDER_LABELS, PAYMENT_TYPE_LABELS, CASTING_REVIEW_STATUS_LABELS } from "@/constants/enumLabels";
import type { CastingReviewStatus, Gender, PaymentType } from "@/types";
import {
  CASTING_CONTACT_FIELD_CONFIG,
  type CastingContactField,
  type CastingFormField,
  type CastingFormState,
} from "@/utils/castingForm";

const REVIEW_STATUS_TRIGGER_CLASS: Record<CastingReviewStatus, string> = {
  PUBLISHED: "select__trigger--booking-confirmed",
  PENDING: "select__trigger--booking-pending",
  REJECTED: "select__trigger--booking-rejected",
};

interface CastingFormFieldsProps {
  form: CastingFormState;
  fieldErrors: Partial<Record<CastingFormField, string>>;
  contactError: string | null;
  contactFieldErrors?: Partial<Record<CastingContactField, string>>;
  showReviewStatus: boolean;
  onFieldChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGenderChange: (value: Gender) => void;
  onPaymentTypeChange: (value: PaymentType) => void;
  onReviewStatusChange?: (value: CastingReviewStatus) => void;
}

/**
 * Shared casting form fields for backoffice and public submission
 */
export function CastingFormFields({
  form,
  fieldErrors,
  contactError,
  contactFieldErrors = {},
  showReviewStatus,
  onFieldChange,
  onContactChange,
  onGenderChange,
  onPaymentTypeChange,
  onReviewStatusChange,
}: CastingFormFieldsProps) {
  return (
    <>
      <CmsField
        label="Título"
        id="title"
        name="title"
        value={form.title}
        onChange={onFieldChange}
        requiredMark
        error={fieldErrors.title}
        hint="Nombre representativo de la convocatoria a publicar."
        placeholder="Ej: Casting de actores para cortometraje de terror"
      />
      <CmsField
        label="Fisic to rol"
        id="fisic"
        name="fisic"
        value={form.fisic}
        onChange={onFieldChange}
        hint="Características físicas del personaje a interpretar."
        placeholder="Ej: Persona alta, cabello largo y castaño, etc."
      />
      <CmsSelectField
        label="Género"
        hint="Expresión del género del personaje a interpretar."
        options={GENDER_LABELS}
        value={form.gender}
        motionScale="lg"
        onChange={(value) => onGenderChange(value as Gender)}
      />
      <CmsSelectField
        label="Tipo de pago"
        hint="Forma en que se compensará al actor por su participación."
        options={PAYMENT_TYPE_LABELS}
        value={form.paymentType}
        motionScale="lg"
        onChange={(value) => onPaymentTypeChange(value as PaymentType)}
      />
      <CmsField
        label="Descripción"
        id="description"
        name="description"
        value={form.description}
        onChange={onFieldChange}
        multiline
        requiredMark
        error={fieldErrors.description}
        hint="Agregá información clara y concisa sobre la propuesta de casting."
        placeholder="Ej: Somos un grupo de estudiantes de cine grabando un corto clase B de terror sobre mujeres lobos. A grabar en zona norte."
      />
      <CmsField
        label="Información requerida"
        id="requiredInfo"
        name="requiredInfo"
        value={form.requiredInfo}
        onChange={onFieldChange}
        multiline
        requiredMark
        hint="Qué se pide para participar en el casting. Separá ítems con comas."
        error={fieldErrors.requiredInfo}
        placeholder="Ej: Foto, video, cv, etc."
      />
      <CmsFieldset
        legend="Medios de contacto (Se requiere al menos uno)"
        groupError={contactError}
      >
        {CASTING_CONTACT_FIELD_CONFIG.map((field) => (
          <CmsField
            key={field.id}
            label={field.label}
            id={field.id}
            name={field.id}
            type={field.type}
            value={form.contact[field.id as CastingContactField]}
            placeholder={field.placeholder}
            onChange={onContactChange}
            hint={field.hint}
            error={contactFieldErrors[field.id]}
            colSpan={field.colSpan}
          />
        ))}
      </CmsFieldset>
      <CmsDateField
        label="Fecha de cierre"
        id="endDate"
        name="endDate"
        type="date"
        value={form.endDate}
        onChange={onFieldChange}
        requiredMark
        hint="Hasta cuando mostrar la convocatoria en el sitio público."
        error={fieldErrors.endDate}
        colSpan={showReviewStatus && onReviewStatusChange ? 3 : 6}
      />
      {showReviewStatus && onReviewStatusChange && (
        <CmsSelectField
          label="Estado"
          hint="Solo los castings publicados se muestran en el sitio público."
          options={CASTING_REVIEW_STATUS_LABELS}
          value={form.reviewStatus}
          motionScale="lg"
          triggerClassName={REVIEW_STATUS_TRIGGER_CLASS[form.reviewStatus]}
          onChange={(value) =>
            onReviewStatusChange(value as CastingReviewStatus)
          }
        />
      )}
    </>
  );
}
